import { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { __OPERATION__BATCH__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesBatchManagement } from '..';
import { useFormik } from 'formik';
import { BatchManagementClient } from './types';
import { transformDataset } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonBatchManagementCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaBatchFilter } from './constants';
import ListHeader from 'shell/components/shared/list-header';
import FilterBatchManagement from 'system/components/batch-management/list';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { Job } from 'shell/models/job-item';
import { transformerJobs } from '../utils/transformer-jobs';
import { Typography } from '@mui/material';
import { FiltersBatchManagementVM } from 'system/view-models/batch-managment/filters-vm';

const BatchManagementList = (): React.ReactElement => {
  const totalElementsRef = useRef<number>(0);

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BatchManagementClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<BatchManagementClient>>({
    element: null,
    open: false,
  });

  const deleteJob = useHttpClient(`${__OPERATION__BATCH__}/jobs/`, {
    options: {
      errorCatching: true,
      method: 'DELETE',
      loading: true,
      data: {},
    },
  });

  const [jobs, getJobs] = useHttpFetch<HttpResponseList<Job>, Array<BatchManagementClient>>(
    `${__OPERATION__BATCH__}/jobs`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: [],
      },
      transformer: (response) => {
        totalElementsRef.current = response.data.payload?.pageInfo?.totalCount || 0;
        return transformerJobs(response);
      },
    }
  );

  const filterTable = useFilterTable<FiltersBatchManagementVM>();

  const onSubmit = useCallback(
    (formValues: FiltersBatchManagementVM) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterBatchManagementForm.setFieldValue('page', 0);
      getJobs({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getJobs]
  );

  const filterBatchManagementForm = useFormik({
    initialValues: initialValuesBatchManagement,
    validationSchema: validationSchemaBatchFilter,
    onSubmit,
  });

  useEffect(() => {
    filterBatchManagementForm.setValues(filterTable.values || initialValuesBatchManagement);
    const queryString = transformDataset(filterTable.values || initialValuesBatchManagement);
    getJobs({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterBatchManagementForm.values);
  }, [filterBatchManagementForm.values]);

  const count: number = useMemo(() => {
    return totalElementsRef.current || 0;
  }, [jobs.data]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<BatchManagementClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/gestione-batch/dettaglio/:id', { id: cell.id.toString() }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<BatchManagementClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/gestione-batch/modifica/:id', { id: cell.id.toString() }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<BatchManagementClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<BatchManagementClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterBatchManagementForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getJobs({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Job>, Array<BatchManagementClient>>
      ).then(() => {
        filterBatchManagementForm.setFieldValue('page', newPage);
      });
    },
    [getJobs, filterBatchManagementForm.setFieldValue, filterBatchManagementForm.values]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterBatchManagementForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getJobs({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Job>, Array<BatchManagementClient>>
      ).then(() => {
        filterBatchManagementForm.setValues(newDataset);
      });
    },
    [filterBatchManagementForm.setFieldValue, filterBatchManagementForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterBatchManagementForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getJobs({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Job>, Array<BatchManagementClient>>
      ).then(() => {
        filterBatchManagementForm.setFieldValue('orders', orders);
      });
    },
    [getJobs, filterBatchManagementForm.setFieldValue, filterBatchManagementForm.values]
  );

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    (
      deleteJob({
        url: `/${deleteAlertModal.element?.id}`,
      }) as ResponsePromise<void>
    ).then(() => {
      setDeleteAlertModal({ open: false, element: null });
      setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
      toast.success('Operazione eseguita con successo');
    });
  }, [setDeleteAlertModal, deleteJob, setDeleteSuccessModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onCloseDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterBatchManagementForm.values, page: 0 });
    getJobs({
      params: {},
      paramsSerializer: () => queryString,
    });
    setDeleteSuccessModal({ open: false, element: null });
  }, [getJobs, setDeleteSuccessModal, filterBatchManagementForm.values]);

  const onOkDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterBatchManagementForm.values, page: 0 });
    getJobs({
      params: {},
      paramsSerializer: () => queryString,
    });
    setDeleteSuccessModal({ open: false, element: null });
  }, [getJobs, setDeleteSuccessModal, filterBatchManagementForm.values]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonBatchManagementCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo batch"
        addBtnLabel="Aggiungi nuovo batch"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/gestione-batch/nuovo');
        }}
        title="Gestione Batch"
      />
      <div className="pmi-my-3">
        <FilterBatchManagement filterBatchForm={filterBatchManagementForm} />
      </div>
      {jobs.data ? (
        (jobs.data as Array<BatchManagementClient>)?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterBatchManagementForm.values.orders}
            activeActions={auths}
            page={filterBatchManagementForm.values.page}
            rowsPerPage={filterBatchManagementForm.values.rowsPerPage}
            body={(jobs.data as Array<BatchManagementClient>) || []}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            isServer
            isNotLayoutFixed
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Batch"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Batch"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Batch"
        message={`La cancellazione del batch ${deleteSuccessModal.element?.id} è avvenuta con successo.`}
        onClose={onCloseDeleteSuccessModal}
        onOk={onOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

BatchManagementList.displayName = 'BatchManagement List';

export default BatchManagementList;
